import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { CATEGORIES } from "../constants";
import ListingCard from "../components/ListingCard";
import { Button } from "../components/ui/button";
import { Gavel, Repeat, Shield, Zap, ArrowRight, MapPin, Plus } from "lucide-react";
import { listingsAPI, auctionsAPI, categoriesAPI } from "../lib/api";
import InstallAppSection from "../components/InstallAppSection";
import ListingOfMonthBanner from "../components/ListingOfMonthBanner";
import SocialSection from "../components/SocialSection";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [cats, setCats] = useState(CATEGORIES);

  useEffect(() => {
    listingsAPI.featured().then(setFeatured).catch(() => {});
    listingsAPI.list({ sort: "recent", limit: 8 }).then(setRecent).catch(() => {});
    auctionsAPI.list().then(setAuctions).catch(() => {});
    categoriesAPI.list().then((list) => {
      // merge counts with local cat data (colors/icons)
      setCats(CATEGORIES.map(c => {
        const found = list.find(x => x.id === c.id);
        return { ...c, count: found?.count || 0 };
      }));
    }).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#faf4e8] to-[#f5ebd9] border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10]" style={{backgroundImage: `url(https://images.pexels.com/photos/18876845/pexels-photo-18876845.jpeg)`, backgroundSize: "cover", backgroundPosition: "center"}} />
        <div className="relative max-w-7xl mx-auto px-4 py-10 md:py-14 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#e86a24]/15 text-[#c8561a] px-3 py-1 rounded-full text-xs font-bold mb-3">
              <MapPin className="w-3 h-3"/> Esclusivo Repubblica di San Marino
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-[#1d3d3a] leading-tight">
              Il mercato del <span className="text-[#e86a24]">Titano</span>.<br/>
              Compra, vendi, <span className="text-[#1d3d3a]">baratta</span>.
            </h1>
            <p className="mt-4 text-slate-600 max-w-lg">
              Pubblica il tuo annuncio in 30 secondi. Solo foto, prezzo e castello. Ricevi offerte, proposte di baratto o partecipa alle aste live.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pubblica">
                <Button size="lg" className="bg-[#e86a24] hover:bg-[#c8561a] rounded-full shadow-lg shadow-orange-500/20">
                  <Plus className="w-5 h-5 mr-1" /> Pubblica annuncio
                </Button>
              </Link>
              <Link to="/aste">
                <Button size="lg" variant="outline" className="rounded-full border-2 border-[#1d3d3a] text-[#1d3d3a] hover:bg-[#1d3d3a] hover:text-white">
                  <Gavel className="w-5 h-5 mr-1" /> Aste Live
                </Button>
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
              <Feature icon={Zap} label="Upload veloce" />
              <Feature icon={Repeat} label="Baratto + €" />
              <Feature icon={Shield} label="Pagamento sicuro" />
            </div>
          </div>
          <div className="hidden md:block relative">
            <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl border-8 border-white rotate-2">
              <img src="https://images.pexels.com/photos/18876845/pexels-photo-18876845.jpeg" alt="San Marino" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-2xl shadow-xl border border-slate-200">
              <div className="text-xs text-slate-500">Annunci attivi</div>
              <div className="text-2xl font-black text-[#e86a24]">{recent.length ? cats.reduce((s,c) => s + (c.count || 0), 0) : "..."}</div>
            </div>
            <div className="absolute -top-4 -right-4 bg-[#1d3d3a] text-white p-4 rounded-2xl shadow-xl">
              <div className="text-xs opacity-80">Aste attive</div>
              <div className="text-2xl font-black">{auctions.length}</div>
            </div>
          </div>
        </div>
      </section>

      {/* LISTING OF THE MONTH (admin-curated) */}
      <ListingOfMonthBanner />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex justify-between items-end mb-5">
          <div>
            <h2 className="text-2xl font-black text-[#1d3d3a]">Categorie</h2>
            <p className="text-sm text-slate-500">Trova quello che cerchi</p>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {cats.map(cat => {
            const Icon = LucideIcons[cat.icon] || LucideIcons.Package;
            return (
              <Link key={cat.id} to={`/categoria/${cat.id}`} className="category-chip group bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center text-center hover:border-[#e86a24] hover:shadow-md">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-2 ${cat.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="text-xs font-semibold text-slate-800 line-clamp-1">{cat.name}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{cat.count} annunci</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED (VETRINE) */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-end mb-5">
            <div>
              <h2 className="text-2xl font-black text-[#1d3d3a] flex items-center gap-2">
                <span className="inline-block w-1.5 h-6 bg-amber-500 rounded"></span>
                Vetrine Premium
              </h2>
              <p className="text-sm text-slate-500">Annunci in evidenza selezionati</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.slice(0, 8).map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}

      {/* AUCTIONS PREVIEW */}
      {auctions.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-10">
          <div className="flex justify-between items-end mb-5">
            <div>
              <h2 className="text-2xl font-black text-[#1d3d3a] flex items-center gap-2">
                <Gavel className="w-6 h-6 text-[#e86a24]" />
                Aste in corso
              </h2>
              <p className="text-sm text-slate-500">Fai la tua offerta prima che finisca il tempo</p>
            </div>
            <Link to="/aste" className="text-sm font-semibold text-[#e86a24] hover:underline flex items-center gap-1">
              Vedi tutte <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {auctions.slice(0, 4).map(a => <ListingCard key={a.id} auction={a} />)}
          </div>
        </section>
      )}

      {/* RECENT */}
      <section className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-between items-end mb-5">
          <div>
            <h2 className="text-2xl font-black text-[#1d3d3a]">Ultimi annunci</h2>
            <p className="text-sm text-slate-500">Appena pubblicati in Repubblica</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {recent.map(l => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="bg-gradient-to-r from-[#1d3d3a] to-[#2d5350] rounded-3xl p-8 md:p-12 text-white grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-xs font-bold mb-3">
              <Repeat className="w-3 h-3"/> Nuovo · Baratto + Offerta
            </div>
            <h3 className="text-2xl md:text-3xl font-black">Non hai soldi? <span className="text-[#e86a24]">Baratta</span>.<br/>Manca poco? <span className="text-[#e86a24]">Baratta e aggiungi €</span>.</h3>
            <p className="mt-3 text-slate-300 text-sm">Solo su SAN MARINO MARKET puoi combinare un oggetto del tuo con un contributo in denaro per chiudere l'affare.</p>
          </div>
          <div className="flex gap-3 justify-start md:justify-end">
            <Link to="/pubblica">
              <Button size="lg" className="bg-[#e86a24] hover:bg-[#c8561a] rounded-full">Inizia ora</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SOCIAL */}
      <SocialSection />

      {/* INSTALL PWA */}
      <InstallAppSection />
    </div>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-700">
      <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm">
        <Icon className="w-4 h-4 text-[#e86a24]" />
      </div>
      <span className="font-medium">{label}</span>
    </div>
  );
}
